import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  VideoCameraIcon,
  BoltIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  CheckCircleIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const VisionEdgeAIPage: React.FC = () => {
  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="gradient-bg text-white relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-6">
              <VideoCameraIcon className="w-5 h-5" />
              <span className="text-sm font-semibold">Product 4 of 4</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              See Everything. Know Everything. <span className="text-purple-300">Instantly.</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
              Deploy AI-powered video intelligence that processes at the edge—real-time insights for security, safety, quality control, and operations without sending video to the cloud.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#demo"
                className="inline-flex items-center space-x-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                <span>Request Demo</span>
              </a>
              <a
                href="#use-cases"
                className="inline-flex items-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-200"
              >
                <span>Explore Use Cases</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Real-Time Video Intelligence at the Edge
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              VisionEdge AI brings YOLO v8-powered computer vision to your cameras and edge devices, delivering real-time object detection, safety monitoring, and operational intelligence while keeping all video data on your infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* Key Value Props Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-blue-500"
            >
              <BoltIcon className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Edge Processing</h3>
              <p className="text-gray-700">
                Real-time analysis at the camera—sub-second latency with no cloud upload required
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-green-500"
            >
              <ShieldCheckIcon className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Video Privacy</h3>
              <p className="text-gray-700">
                Your video never leaves your premises—critical for sensitive locations and compliance
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-purple-500"
            >
              <CpuChipIcon className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Custom Object Detection</h3>
              <p className="text-gray-700">
                Train models to recognize your specific objects, scenarios, and safety conditions
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-orange-500"
            >
              <EyeIcon className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Industry Ready</h3>
              <p className="text-gray-700">
                From PPE compliance to security to quality control—one platform, countless applications
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Primary Use Cases
            </h2>
          </div>

          <div className="space-y-8">
            {/* Security */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl shadow-lg"
            >
              <div className="flex items-center space-x-3 mb-4">
                <ShieldCheckIcon className="w-10 h-10 text-blue-600" />
                <h3 className="text-2xl font-bold text-gray-900">Security</h3>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Intrusion detection</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Perimeter monitoring</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Unauthorized access alerts</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Facial recognition</span>
                </div>
              </div>
            </motion.div>

            {/* Manufacturing */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl shadow-lg"
            >
              <div className="flex items-center space-x-3 mb-4">
                <CpuChipIcon className="w-10 h-10 text-green-600" />
                <h3 className="text-2xl font-bold text-gray-900">Manufacturing</h3>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Quality control</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Safety compliance (PPE)</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Defect detection</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Equipment monitoring</span>
                </div>
              </div>
            </motion.div>

            {/* Construction */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl shadow-lg"
            >
              <div className="flex items-center space-x-3 mb-4">
                <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="text-2xl font-bold text-gray-900">Construction</h3>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">PPE monitoring</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Site safety</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Progress tracking</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Equipment monitoring</span>
                </div>
              </div>
            </motion.div>

            {/* Logistics */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-2xl shadow-lg"
            >
              <div className="flex items-center space-x-3 mb-4">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                <h3 className="text-2xl font-bold text-gray-900">Logistics</h3>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Dock monitoring</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Package tracking</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Loading verification</span>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Inventory visibility</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Target Industries Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Built for Multiple Industries
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-2xl transition-shadow"
            >
              <div className="text-4xl mb-3">🏭</div>
              <h3 className="text-lg font-bold text-gray-900">Manufacturing</h3>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-2xl transition-shadow"
            >
              <div className="text-4xl mb-3">🏗️</div>
              <h3 className="text-lg font-bold text-gray-900">Construction</h3>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-2xl transition-shadow"
            >
              <div className="text-4xl mb-3">🛡️</div>
              <h3 className="text-lg font-bold text-gray-900">Security</h3>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-2xl transition-shadow"
            >
              <div className="text-4xl mb-3">📦</div>
              <h3 className="text-lg font-bold text-gray-900">Logistics</h3>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-2xl transition-shadow"
            >
              <div className="text-4xl mb-3">🛍️</div>
              <h3 className="text-lg font-bold text-gray-900">Retail</h3>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technical Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Powered by YOLO v8
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              State-of-the-art object detection running at the edge
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-6 rounded-xl"
            >
              <div className="text-3xl font-bold text-blue-600 mb-2">&lt;1s</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Sub-second Latency</h3>
              <p className="text-gray-700">
                Real-time processing at the edge with minimal delay
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-6 rounded-xl"
            >
              <div className="text-3xl font-bold text-green-600 mb-2">24/7</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Continuous Monitoring</h3>
              <p className="text-gray-700">
                Always-on intelligence for your video streams
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-6 rounded-xl"
            >
              <div className="text-3xl font-bold text-purple-600 mb-2">Custom</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Model Training</h3>
              <p className="text-gray-700">
                Train for your specific objects and scenarios
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-6 rounded-xl"
            >
              <div className="text-3xl font-bold text-orange-600 mb-2">100%</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Data Privacy</h3>
              <p className="text-gray-700">
                Video never leaves your infrastructure
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-6 rounded-xl"
            >
              <div className="text-3xl font-bold text-red-600 mb-2">Multi</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Camera Support</h3>
              <p className="text-gray-700">
                Process multiple video streams simultaneously
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-6 rounded-xl"
            >
              <div className="text-3xl font-bold text-teal-600 mb-2">Alert</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Smart Notifications</h3>
              <p className="text-gray-700">
                Instant alerts for critical events and violations
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Query Your Video Intelligence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              VisionEdge AI integrates with DB Talker for natural language queries
            </p>
          </div>

          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-12 rounded-2xl">
            <h3 className="text-2xl font-bold mb-6 text-center">Example Queries</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <p className="text-lg mb-2">💬 "How many safety violations occurred this week?"</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <p className="text-lg mb-2">💬 "Show PPE compliance rate by department"</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <p className="text-lg mb-2">💬 "List all perimeter breach events today"</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <p className="text-lg mb-2">💬 "What's the defect detection rate this month?"</p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/db-talker"
                className="inline-flex items-center space-x-2 bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                <span>Explore DB Talker Integration</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="demo" className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Deploy Video Intelligence?
          </h2>
          <p className="text-xl mb-8">
            See VisionEdge AI in action with your existing cameras.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/schedule-meeting"
              className="inline-flex items-center space-x-2 bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <span>Request a Demo</span>
            </Link>
            <Link
              to="/schedule-meeting"
              className="inline-flex items-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-all"
            >
              <span>Contact Sales</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisionEdgeAIPage;

